// Create Supabase Project Dialog
// Allows users to create a new Supabase project directly from Pixel

import { useState, useEffect } from 'react';
import { Plus, Globe, Building2, Database } from 'lucide-react';
import { PxMark } from '@/components/PxMark';
import { useSupabaseMCP } from '@/hooks/useSupabaseMCP';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

interface Organization {
    id: string;
    name: string;
    slug: string;
}

interface CreateSupabaseProjectDialogProps {
    trigger?: React.ReactNode;
    pixelProjectId?: string;
    onProjectCreated?: (project: any) => void;
}

const REGIONS = [
    { value: 'eu-west-2', label: 'Paris (eu-west-2)' },
    { value: 'eu-west-1', label: 'Dublin (eu-west-1)' },
    { value: 'eu-central-1', label: 'Francfort (eu-central-1)' },
    { value: 'us-east-1', label: 'Virginie du Nord (us-east-1)' },
    { value: 'us-west-1', label: 'Californie du Nord (us-west-1)' },
    { value: 'ap-southeast-1', label: 'Singapour (ap-southeast-1)' },
    { value: 'ap-northeast-1', label: 'Tokyo (ap-northeast-1)' },
];

export function CreateSupabaseProjectDialog({
    trigger,
    pixelProjectId,
    onProjectCreated,
}: CreateSupabaseProjectDialogProps) {
    const { fetchOrganizations, createProject, isConnected } = useSupabaseMCP();

    const [open, setOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrgs, setLoadingOrgs] = useState(false);

    // Form state
    const [organizationId, setOrganizationId] = useState('');
    const [projectName, setProjectName] = useState('');
    const [region, setRegion] = useState('eu-west-2');
    const [plan, setPlan] = useState<'free' | 'pro'>('free');

    // Load organizations when dialog opens
    useEffect(() => {
        if (open && isConnected && organizations.length === 0) {
            loadOrganizations();
        }
    }, [open, isConnected]);

    const loadOrganizations = async () => {
        setLoadingOrgs(true);
        try {
            const orgs = await fetchOrganizations();
            setOrganizations(orgs);
            if (orgs.length === 1) {
                setOrganizationId(orgs[0].id);
            }
        } catch (error) {
            console.error('Failed to load organizations:', error);
            toast.error('Impossible de charger les organisations');
        } finally {
            setLoadingOrgs(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!organizationId) {
            toast.error('Sélectionnez une organisation');
            return;
        }

        if (!projectName.trim()) {
            toast.error('Entrez un nom de projet');
            return;
        }

        setIsLoading(true);

        try {
            const result = await createProject({
                organization_id: organizationId,
                name: projectName.trim(),
                region,
                plan,
                pixel_project_id: pixelProjectId,
            });

            if (result.success) {
                toast.success('Projet Supabase créé avec succès !');
                setOpen(false);
                onProjectCreated?.(result.project);

                // Reset form
                setProjectName('');
            } else {
                toast.error(result.error || 'Échec de la création du projet');
            }
        } catch (error: any) {
            toast.error(error.message || 'Erreur lors de la création');
        } finally {
            setIsLoading(false);
        }
    };

    if (!isConnected) {
        return null;
    }

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                {trigger || (
                    <Button variant="outline" size="sm" className="gap-2">
                        <Plus className="h-4 w-4" />
                        Créer un projet Supabase
                    </Button>
                )}
            </DialogTrigger>

            <DialogContent className="border-hairline-strong bg-card sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Database className="h-5 w-5 text-muted-foreground" />
                        Créer un projet Supabase
                    </DialogTitle>
                    <DialogDescription>
                        Créez un nouveau projet Supabase directement depuis Pixel.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 py-4">
                    {/* Organization */}
                    <div className="space-y-2">
                        <Label htmlFor="organization" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.1em] text-faint">
                            <Building2 className="h-3.5 w-3.5" />
                            Organisation
                        </Label>
                        {loadingOrgs ? (
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <PxMark state="thinking" size={14} />
                                Chargement des organisations…
                            </div>
                        ) : (
                            <Select value={organizationId} onValueChange={setOrganizationId}>
                                <SelectTrigger id="organization">
                                    <SelectValue placeholder="Sélectionnez une organisation" />
                                </SelectTrigger>
                                <SelectContent>
                                    {organizations.map((org) => (
                                        <SelectItem key={org.id} value={org.id}>
                                            {org.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>

                    {/* Project Name */}
                    <div className="space-y-2">
                        <Label htmlFor="projectName" className="font-mono text-[11px] uppercase tracking-[0.1em] text-faint">Nom du projet</Label>
                        <Input
                            id="projectName"
                            placeholder="mon-super-projet"
                            value={projectName}
                            onChange={(e) => setProjectName(e.target.value)}
                            disabled={isLoading}
                            className="font-mono text-[12px]"
                        />
                    </div>

                    {/* Region */}
                    <div className="space-y-2">
                        <Label htmlFor="region" className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.1em] text-faint">
                            <Globe className="h-3.5 w-3.5" />
                            Région
                        </Label>
                        <Select value={region} onValueChange={setRegion}>
                            <SelectTrigger id="region">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {REGIONS.map((r) => (
                                    <SelectItem key={r.value} value={r.value}>
                                        {r.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Plan */}
                    <div className="space-y-2">
                        <Label htmlFor="plan" className="font-mono text-[11px] uppercase tracking-[0.1em] text-faint">Plan</Label>
                        <Select value={plan} onValueChange={(v) => setPlan(v as 'free' | 'pro')}>
                            <SelectTrigger id="plan">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="free">Free (gratuit)</SelectItem>
                                <SelectItem value="pro">Pro (25 $/mois)</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </form>

                <DialogFooter>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => setOpen(false)}
                        disabled={isLoading}
                    >
                        Annuler
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={isLoading || !organizationId || !projectName.trim()}
                    >
                        {isLoading ? (
                            <>
                                <PxMark state="generating" size={14} className="mr-2" />
                                Création…
                            </>
                        ) : (
                            <>
                                <Plus className="h-4 w-4 mr-2" />
                                Créer le projet
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
